import {
  type WikiChangesCard as CardData,
  CSRF_HEADER,
  changeKindOf,
  changeRequestState,
  changeSizes,
  trustState,
  type WikiChangeItem,
  type WikiChangeSet,
} from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  BookCheck,
  Check,
  Eye,
  FilePen,
  FileText,
  FolderTree,
  Send,
  X,
} from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { InlineError } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { api } from "../api.ts";
import { ApiError, noContent, ok } from "../api-errors.ts";
import { t as i18nT } from "../i18n/i18n.ts";
import { useProject } from "../projects/queries.ts";
import { Link, useOpenWiki, wikiPath } from "../router.tsx";
import { useSession } from "../session.tsx";
import { AuthorPublishSheet } from "./AuthorPublishSheet.tsx";
import { itemName } from "./ChangeSetReview.tsx";
import { useChangeSet, wikiKeys } from "./queries.ts";
import { ResolveTrigger } from "./ResolutionRunCard.tsx";
import type { ResolutionRequestData } from "./resolve.ts";
import { itemSize, sizesText } from "./sizes.ts";

/** The card's DOM id, for the "Updated the draft below" line of earlier turns. */
export const wikiCardId = (changeSetId: string) => `wiki-draft-${changeSetId}`;

/**
 * For each change set, the index of the latest message carrying its card (D8): its live card
 * renders there, earlier messages show "Wiki draft updated below".
 */
export function liveCardIndex(cards: readonly (CardData | null)[]): Map<string, number> {
  const latest = new Map<string, number>();
  cards.forEach((card, index) => {
    if (card) {
      latest.set(card.changeSetId, index);
    }
  });
  return latest;
}

/** At most this many content rows; the rest is "and N more". */
const ROWS = 4;

/**
 * Who decides on `set`, in the author's words (wiki-collaboration-ux D4, D8): the viewer when
 * the trust level lets them publish it, else the level's decider.
 */
export function whoDecides(set: WikiChangeSet): string | null {
  const t = i18nT;
  if (!set.trust) {
    return null;
  }
  if (trustState(set).whole && set.mayPublish !== false) {
    return t("wiki.author.decides.you");
  }
  return t(`wiki.author.decides.${set.trust.reason ?? set.trust.level}`);
}

/**
 * The conversation's live Wiki draft card (wiki-collaboration-ux D8): one per change set, under
 * the latest turn that wrote into it, updated as each `wiki_write` lands and read live from the
 * change-set route. It lists the changes by kind with their size; its creator previews the set
 * beside the conversation, publishes it when the trust level allows (a compact sheet), else
 * submits it for review, or discards it. No reviewer action is ever offered here. Settled, it is
 * one quiet status line (DESIGN.md "Suggestion cards"), never a toast.
 */
export function WikiChangesCard(props: {
  projectId: string;
  card: CardData;
  /** The viewer may draft in this project (`wiki.draft`) and it is not frozen. */
  canDraft: boolean;
  /** Starts Resolve with the agent in this conversation (wiki-resolve-with-agent D1). */
  onResolve?: (request: ResolutionRequestData) => void;
  /** The conversation is answering: a second run would only queue behind it. */
  busy?: boolean;
}) {
  const { projectId, card, canDraft, onResolve } = props;
  const project = useProject(projectId).data;
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken, userId } = useSession();
  const queryClient = useQueryClient();
  const { show } = useOpenWiki();
  const set = useChangeSet(projectId, card.changeSetId);
  const [dialog, setDialog] = useState<"discard" | "publish" | null>(null);
  // Each streamed card (a new write) refreshes the change set and an open panel showing it.
  const signature = `${card.writes ?? 0}:${card.status}:${card.pages}:${card.folders}:${card.stale}:${card.title}`;
  useEffect(() => {
    void signature;
    void queryClient.invalidateQueries({
      queryKey: [...wikiKeys.all(projectId), "change", card.changeSetId],
    });
  }, [signature, queryClient, projectId, card.changeSetId]);

  const endpoint = api.api.projects[":projectId"].wiki["change-sets"][":id"];
  const param = { projectId, id: card.changeSetId };
  const headers = { [CSRF_HEADER]: csrfToken };
  const refresh = () => queryClient.invalidateQueries({ queryKey: wikiKeys.all(projectId) });
  const submit = useMutation({
    mutationFn: async () => ok(await endpoint.submit.$post({ param }, { headers })),
    onSettled: refresh,
  });
  const discard = useMutation({
    mutationFn: async () => noContent(await endpoint.discard.$post({ param }, { headers })),
    onSuccess: () => setDialog(null),
    onSettled: refresh,
  });

  const live = set.data;
  const status = live
    ? live.status === "open" && live.submittedAt === null
      ? "working"
      : live.status
    : card.status;
  const gone = set.error instanceof ApiError && set.error.status === 404;
  const preview = () => show({ kind: "changes", changeSetId: card.changeSetId, itemId: null });

  if (gone) {
    return (
      <p role="status" className="text-xs text-fg-muted">
        {t("wiki.card.unavailable")}
      </p>
    );
  }
  if (status !== "working") {
    // Sizes count every published item (folders included); links go to the published pages.
    const publishedItems = (live?.items ?? []).filter((i) => i.status === "published");
    const published = publishedItems.filter((i) => i.publishedDocumentId);
    const decider = live && status !== "published" && status !== "discarded" && whoDecides(live);
    const line = (
      <p
        role="status"
        {...(!(live?.resolve && onResolve && canDraft) && { id: wikiCardId(card.changeSetId) })}
        className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-muted"
      >
        <BookCheck aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        <span>
          {t(`wiki.author.settled.${status}`)}
          {/* Written into after submission: it stays submitted and is checked again. */}
          {status === "open" && card.status === "open" ? ` · ${t("wiki.author.revised")}` : ""}
          {decider ? ` · ${decider}` : ""}
          {live && status === "published"
            ? ` · ${sizesText(changeSizes({ items: publishedItems }))}`
            : ""}
        </span>
        {status === "published" && published.length ? (
          published.slice(0, ROWS).map((item) => (
            <Link
              key={item.id}
              href={wikiPath(
                projectId,
                item.publishedDocumentId as string,
                item.publishedVersion ?? undefined,
              )}
              className="text-accent-text underline-offset-4 hover:underline"
            >
              {item.title}
            </Link>
          ))
        ) : status === "discarded" ? null : (
          <button
            type="button"
            onClick={preview}
            className="text-accent-text underline-offset-4 hover:underline"
          >
            {t("wiki.author.viewChanges")}
          </button>
        )}
      </p>
    );
    // The reviewers' open comments, for the set's creator (wiki-resolve-with-agent D1, D7).
    if (live?.resolve && onResolve && canDraft) {
      return (
        <div id={wikiCardId(card.changeSetId)} className="flex scroll-mt-6 flex-col gap-2">
          {line}
          <ResolveTrigger
            set={live}
            busy={props.busy === true}
            hint
            onResolve={() =>
              onResolve({
                changeSetId: live.id,
                title: live.hiddenItems ? "" : live.title,
                threadCount: live.resolve?.count ?? 0,
              })
            }
          />
        </div>
      );
    }
    return line;
  }

  const mine = live ? live.createdBy.toLowerCase() === userId.toLowerCase() : false;
  const author = mine && canDraft;
  const sizes = live ? changeSizes(live) : null;
  const stale = live ? live.items.filter((i) => i.status === "stale").length : card.stale;
  const title =
    (live ? (live.hiddenItems ? "" : live.title) : card.title) || t("wiki.card.untitled");
  const decides =
    !!live &&
    trustState(live).whole &&
    live.mayPublish !== false &&
    live.trust !== undefined &&
    !live.hiddenItems;
  const publishable =
    !!live &&
    stale === 0 &&
    !changeRequestState(live).publishBlocked &&
    live.items.some((i) => i.status === "pending" || i.status === "approved");
  const decider = live ? whoDecides(live) : null;
  const error = submit.error ?? discard.error;
  const busy = submit.isPending || discard.isPending;
  return (
    <section
      id={wikiCardId(card.changeSetId)}
      data-esi-content="wiki"
      aria-labelledby={`${id}-title`}
      className={cn(
        "flex scroll-mt-6 flex-col gap-2.5 rounded-lg border border-dashed border-line-strong px-3.5 py-3",
        "transition-colors duration-(--duration-fast) ease-(--ease-out) focus-within:border-solid",
      )}
    >
      <div className="flex items-start gap-2.5">
        <FilePen
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-fg-muted"
          strokeWidth={1.75}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h3 id={`${id}-title`} className="text-[13px] font-medium text-fg-secondary">
            {mine ? t("wiki.author.titleOnlyYou") : t("wiki.author.title")}
          </h3>
          <p className="break-words text-sm font-medium text-fg">{title}</p>
          <p
            aria-live="polite"
            className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-fg-muted"
          >
            <span className="tabular-nums">
              {sizes ? sizesText(sizes) : t("wiki.card.pages", { count: card.pages })}
            </span>
            {stale ? (
              <>
                <span aria-hidden="true">·</span>
                <span className="tabular-nums">{t("wiki.card.stale", { count: stale })}</span>
              </>
            ) : null}
            {mine && decider ? (
              <>
                <span aria-hidden="true">·</span>
                <span>{decider}</span>
              </>
            ) : null}
          </p>
        </div>
      </div>
      {live ? <ChangeRows items={live.items} /> : null}
      {error ? (
        <InlineError>{error instanceof ApiError ? error.message : t("errors.network")}</InlineError>
      ) : null}
      <div className="flex flex-wrap items-center gap-1.5 pl-6.5">
        <Button type="button" size="sm" variant="outline" onClick={preview}>
          <Eye aria-hidden="true" strokeWidth={1.75} />
          {t("wiki.author.preview")}
        </Button>
        {author && decides && project ? (
          <>
            <Button
              type="button"
              size="sm"
              disabled={busy || !publishable}
              onClick={() => setDialog("publish")}
            >
              <Check aria-hidden="true" strokeWidth={1.75} />
              {t("wiki.author.publish")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="text-fg-secondary"
              disabled={busy}
              onClick={() => submit.mutate()}
            >
              {t("wiki.author.submitInstead")}
            </Button>
          </>
        ) : author ? (
          <Button type="button" size="sm" disabled={busy} onClick={() => submit.mutate()}>
            <Send aria-hidden="true" strokeWidth={1.75} />
            {t("wiki.author.submit")}
          </Button>
        ) : null}
        {author ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            className="text-fg-secondary"
            onClick={() => setDialog("discard")}
          >
            <X aria-hidden="true" strokeWidth={1.75} />
            {t("wiki.card.discard")}
          </Button>
        ) : null}
      </div>
      <AppDialog
        open={dialog === "discard"}
        onClose={() => setDialog(null)}
        title={t("wiki.card.discardTitle")}
      >
        <p className="text-sm text-fg-secondary">{t("wiki.card.discardBody")}</p>
        {discard.isError ? <InlineError>{discard.error.message}</InlineError> : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setDialog(null)}>
            {t("wiki.editor.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={discard.isPending}
            onClick={() => discard.mutate()}
          >
            {t("wiki.card.discard")}
          </Button>
        </DialogFooter>
      </AppDialog>
      {live && project ? (
        <AuthorPublishSheet
          open={dialog === "publish"}
          onClose={() => setDialog(null)}
          project={project}
          changeSet={live}
        />
      ) : null}
    </section>
  );
}

/**
 * The changes by kind, one line each (mock S1): content items with their size, then the
 * structure (folders, moves) folded into one row, never as heavy as a page.
 */
function ChangeRows({ items }: { items: readonly WikiChangeItem[] }) {
  const { t } = useTranslation();
  const content = items.filter((i) => !["moves", "folders"].includes(changeKindOf(i.op)));
  const structure = items.length - content.length;
  const shown = content.slice(0, ROWS);
  const more = content.length - shown.length;
  if (!items.length) {
    return null;
  }
  const structureSizes = changeSizes({
    items: items.filter((i) => ["moves", "folders"].includes(changeKindOf(i.op))),
  });
  return (
    <ul className="ml-6.5 flex flex-col divide-y divide-line rounded-md border border-line bg-surface-raised">
      {shown.map((item) => {
        const size = itemSize(item);
        return (
          <li key={item.id} className="flex min-w-0 items-center gap-2 px-2.5 py-1.5 text-xs">
            <span className="w-16 shrink-0 text-fg-muted">
              {t(`wiki.author.kind.${changeKindOf(item.op)}`)}
            </span>
            <FileText
              aria-hidden="true"
              className="size-3.5 shrink-0 text-fg-muted"
              strokeWidth={1.75}
            />
            <span className="min-w-0 flex-1 truncate text-fg">{itemName(item)}</span>
            {size ? <SizeText item={item} text={size} /> : null}
          </li>
        );
      })}
      {more > 0 ? (
        <li className="px-2.5 py-1.5 text-xs text-fg-muted">
          {t("wiki.author.more", { count: more })}
        </li>
      ) : null}
      {structure > 0 ? (
        <li className="flex min-w-0 items-center gap-2 px-2.5 py-1.5 text-xs">
          <span className="w-16 shrink-0 text-fg-muted">{t("wiki.author.structure")}</span>
          <FolderTree
            aria-hidden="true"
            className="size-3.5 shrink-0 text-fg-muted"
            strokeWidth={1.75}
          />
          <span className="min-w-0 flex-1 truncate text-fg">{sizesText(structureSizes)}</span>
        </li>
      ) : null}
    </ul>
  );
}

/** "+4 −1" with the plus and minus tinted (evidence, never the only cue), or "38 lines". */
function SizeText({ item, text }: { item: WikiChangeItem; text: string }) {
  if (item.op === "edit" && item.stats) {
    return (
      <span className="shrink-0 tabular-nums">
        <span aria-hidden="true">
          <span className="text-success-fg">+{item.stats.added}</span>{" "}
          <span className="text-danger-fg">−{item.stats.removed}</span>
        </span>
        <span className="sr-only">{text}</span>
      </span>
    );
  }
  return <span className="shrink-0 text-fg-muted tabular-nums">{text}</span>;
}

/** An earlier turn's trace of a set whose live card sits under a later turn (D8). */
export function WikiDraftUpdatedLine({ changeSetId }: { changeSetId: string }) {
  const { t } = useTranslation();
  return (
    <p className="flex items-center gap-1.5 text-xs text-fg-muted">
      <FilePen aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
      <a
        href={`#${wikiCardId(changeSetId)}`}
        className="underline-offset-4 hover:text-fg-secondary hover:underline"
        onClick={(e) => {
          e.preventDefault();
          document
            .getElementById(wikiCardId(changeSetId))
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        }}
      >
        {t("wiki.author.updatedBelow")}
      </a>
      <ArrowDown aria-hidden="true" className="size-3" strokeWidth={1.75} />
    </p>
  );
}
